import React from 'react';
import { ConfirmModalV3 } from 'Components/modals/ConfirmModal';
import useModalV3 from 'Hooks/useModalV3';
import { useHistory } from 'react-router-dom';
import { Prompt } from 'react-router';

export type SaveChangesPromptProps = {
  /** The title of the modal */
  title: string;

  /** The subtitle of the modal */
  subtitle: string;

  /** The text for the Cancel button */
  cancelBtnText?: string;

  /** The text for the Confirm button */
  confirmBtnText?: string;

  /** The condition that should be met for the modal to display */
  when: boolean;
};

const SaveChangesPromptV3: React.FC<SaveChangesPromptProps> = ({
  title,
  subtitle,
  cancelBtnText = 'Cancel',
  confirmBtnText = 'Confirm',
  when,
}) => {
  const { showModal, hideModal } = useModalV3();
  const history = useHistory();
  const [promptUserBeforeLeaving, setPromptUserBeforeLeaving] = React.useState(true);

  const handleNavigation = React.useCallback(
    navigationData => {
      // handles parameters, so the prompt isn't shown if the base url hasn't changed
      if (navigationData.pathname !== history.location.pathname) {
        setPromptUserBeforeLeaving(false);
        showModal(
          <ConfirmModalV3
            subtitle={subtitle}
            cancelBtnText={cancelBtnText}
            confirmBtnText={confirmBtnText}
            onConfirm={() => {
              const targetPath = navigationData.search
                ? `${navigationData.pathname}${navigationData.search}`
                : navigationData.pathname;
              history.push(targetPath);
              hideModal();
            }}
            onClose={() => {
              setPromptUserBeforeLeaving(true);
              hideModal();
            }}
          />,
          {
            title,
          },
        );
        return false; // prevents default navigation behavior, so ConfirmModal can control it
      }
      return true;
    },
    [cancelBtnText, confirmBtnText, hideModal, history, showModal, subtitle, title],
  );

  React.useEffect(() => {
    const preventUnload = (event: BeforeUnloadEvent) => {
      if (when) {
        event.preventDefault();
        // eslint-disable-next-line no-param-reassign
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => {
      window.removeEventListener('beforeunload', preventUnload);
    };
  }, [handleNavigation, when]);

  return <Prompt when={when && promptUserBeforeLeaving} message={handleNavigation} />;
};

export default SaveChangesPromptV3;
